import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';
import { loadFont as loadMontserrat } from '@remotion/google-fonts/MontserratAlternates';
import { loadFont as loadNoto } from '@remotion/google-fonts/NotoSansDevanagari';

const { fontFamily: mont } = loadMontserrat('normal', { weights: ['900'], subsets: ['latin'] });
const { fontFamily: noto } = loadNoto('normal', { weights: ['700'], subsets: ['devanagari'] });

const FONT_SIZE = 88;
const GOLD = '#FFD700';

// ── River background — top-left narrow → bottom-right wide, behind text ────────
const RiverBg: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const reveal = interpolate(frame, [0, Math.round(fps * 5)], [0, 1], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  // Shimmer bands move diagonally along the river
  const flowOffset = (frame * 1) % 1280;
  const shimmer1 = 0.10 + 0.05 * Math.sin(frame * 0.08);
  const shimmer2 = 0.07 + 0.04 * Math.sin(frame * 0.12 + 1.5);

  // Meandering river — 3 S-bends, ~35px wide at top-left, ~200px at bottom-right
  // Upper edge (left→right):
  const UPPER = 'M 0,0 C 80,0 160,80 220,130 C 280,180 340,120 420,160 C 500,200 580,290 680,330 C 780,370 840,310 940,360 C 1040,410 1150,440 1280,460';
  // Full closed river shape — upper edge then lower edge reversed:
  const RIVER = `${UPPER} L 1280,660 C 1160,630 1050,580 950,520 C 850,460 780,510 680,450 C 580,390 500,275 420,230 C 340,185 280,235 220,180 C 160,125 80,35 0,35 Z`;
  const UPPER_EDGE = UPPER;
  const LOWER_EDGE = 'M 0,35 C 80,35 160,125 220,180 C 280,235 340,185 420,230 C 500,275 580,390 680,450 C 780,510 850,460 950,520 C 1050,580 1160,630 1280,660';

  return (
    <svg
      width={1280} height={704} viewBox="0 0 1280 704"
      style={{ position: 'absolute', inset: 0, zIndex: 0 }}
    >
      <defs>
        <linearGradient id="rg-base" x1="0" y1="0" x2="1280" y2="704" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#0EA5E9" stopOpacity="0.06" />
          <stop offset="30%"  stopColor="#38BDF8" stopOpacity="0.20" />
          <stop offset="65%"  stopColor="#7DD3FC" stopOpacity="0.24" />
          <stop offset="100%" stopColor="#BAE6FD" stopOpacity="0.16" />
        </linearGradient>

        {/* Moving shimmer band 1 */}
        <linearGradient id="rg-flow1"
          x1={flowOffset} y1="0" x2={flowOffset + 380} y2="0"
          gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#E0F2FE" stopOpacity="0" />
          <stop offset="40%"  stopColor="#BAE6FD" stopOpacity="0.45" />
          <stop offset="70%"  stopColor="#E0F2FE" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#FFFFFF"  stopOpacity="0" />
        </linearGradient>

        {/* Moving shimmer band 2 — offset phase */}
        <linearGradient id="rg-flow2"
          x1={(flowOffset + 560) % 1400} y1="0" x2={(flowOffset + 900) % 1400} y2="0"
          gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#E0F2FE" stopOpacity="0" />
          <stop offset="50%"  stopColor="#7DD3FC" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#E0F2FE" stopOpacity="0" />
        </linearGradient>

        <clipPath id="river-reveal">
          <rect x="0" y="0" width={1280 * reveal} height="704" />
        </clipPath>

        <filter id="river-glow" x="-5%" y="-5%" width="110%" height="110%">
          <feGaussianBlur stdDeviation="10" />
        </filter>
      </defs>

      <g clipPath="url(#river-reveal)">
        {/* Soft glow halo under river */}
        <path d={RIVER} fill="#38BDF8" opacity={0.10} filter="url(#river-glow)" />
        {/* Base fill */}
        <path d={RIVER} fill="url(#rg-base)" />
        {/* Shimmer layer 1 */}
        <path d={RIVER} fill="url(#rg-flow1)" opacity={shimmer1} />
        {/* Shimmer layer 2 */}
        <path d={RIVER} fill="url(#rg-flow2)" opacity={shimmer2} />
        {/* Upper edge highlight */}
        <path d={UPPER_EDGE} fill="none" stroke="rgba(186,230,253,0.45)" strokeWidth="2" />
        {/* Lower edge subtle line */}
        <path d={LOWER_EDGE} fill="none" stroke="rgba(125,211,252,0.20)" strokeWidth="1.5" />
      </g>
    </svg>
  );
};

// ── Lotus ──────────────────────────────────────────────────────────────────────
const Lotus: React.FC<{ cx: number; cy: number; r: number; startFrame: number; uid: string }> = ({ cx, cy, r, startFrame, uid }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relFrame = frame - startFrame;
  if (relFrame < 0) return null;
  const sc = spring({ fps, frame: Math.max(0, relFrame), config: { damping: 13, stiffness: 110, mass: 0.8 }, durationInFrames: 18 });
  const pulse = 1 + 0.035 * Math.sin(frame * 0.18);
  return (
    <g transform={`translate(${cx}, ${cy}) scale(${sc * pulse})`}>
      <defs>
        <linearGradient id={`lg-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#FFFFF0" />
          <stop offset="45%"  stopColor={GOLD} />
          <stop offset="100%" stopColor="#C8860A" />
        </linearGradient>
        <filter id={`lf-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {Array.from({ length: 8 }).map((_, i) => (
        <ellipse key={i} cx={0} cy={-r * 0.55} rx={r * 0.24} ry={r * 0.6}
          fill={`url(#lg-${uid})`} stroke="rgba(255,200,0,0.4)" strokeWidth={0.5} opacity={0.88}
          transform={`rotate(${i * 45})`} />
      ))}
      {Array.from({ length: 4 }).map((_, i) => (
        <ellipse key={i} cx={0} cy={-r * 0.22} rx={r * 0.17} ry={r * 0.32}
          fill="#FFFDE0" opacity={0.92} transform={`rotate(${i * 90 + 22.5})`} />
      ))}
      <circle cx={0} cy={0} r={r * 0.12} fill="#FFF8C0" filter={`url(#lf-${uid})`} />
    </g>
  );
};

// ── Water wave symbol (3 animated gold S-curves stacked) ───────────────────────
const WaterWave: React.FC<{ cx: number; cy: number; startFrame: number; glow: number }> = ({ cx, cy, startFrame, glow }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relFrame = frame - startFrame;
  if (relFrame < 0) return null;
  const sc = spring({ fps, frame: Math.max(0, relFrame), config: { damping: 14, stiffness: 100, mass: 0.9 }, durationInFrames: 22 });
  const pulse = 1 + 0.025 * Math.sin(frame * 0.2);
  const wavePhase = frame * 0.14;
  const w = 90;

  return (
    <g transform={`translate(${cx}, ${cy}) scale(${sc * pulse})`}>
      <defs>
        <linearGradient id="ww-gold" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#B8860B" />
          <stop offset="50%"  stopColor={GOLD} />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
        <filter id="ww-glow">
          <feGaussianBlur stdDeviation={1 + glow * 2} result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {([
        { yOff: -20, amp: 12, sw: 4.5 },
        { yOff:   0, amp: 10, sw: 3.5 },
        { yOff:  20, amp:  8, sw: 2.5 },
      ] as { yOff: number; amp: number; sw: number }[]).map(({ yOff, amp, sw }, i) => {
        const shift = Math.sin(wavePhase + i * 1.1) * 3;
        const d = `M ${-w / 2},${yOff + shift} C ${-w / 4},${yOff - amp + shift} ${w / 4},${yOff + amp - shift} ${w / 2},${yOff + shift}`;
        return (
          <path
            key={i}
            d={d}
            fill="none"
            stroke="url(#ww-gold)"
            strokeWidth={sw}
            strokeLinecap="round"
            opacity={0.92 - i * 0.12}
            filter="url(#ww-glow)"
          />
        );
      })}
    </g>
  );
};

// ── Sparkle ────────────────────────────────────────────────────────────────────
const Sparkle: React.FC<{ cx: number; cy: number; r: number; phase: number }> = ({ cx, cy, r, phase }) => {
  const frame = useCurrentFrame();
  const t = (frame * 1.2 + phase) % 64;
  const op = interpolate(t, [0, 10, 32, 54, 64], [0, 0.8, 0.35, 0.8, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const sc = interpolate(t, [0, 10, 32, 54, 64], [0.5, 1.1, 0.8, 1.1, 0.5], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const s = r * sc;
  return (
    <polygon
      points={`${cx},${cy - s * 2.2} ${cx + s * 0.45},${cy - s * 0.45} ${cx + s * 2.2},${cy} ${cx + s * 0.45},${cy + s * 0.45} ${cx},${cy + s * 2.2} ${cx - s * 0.45},${cy + s * 0.45} ${cx - s * 2.2},${cy} ${cx - s * 0.45},${cy - s * 0.45}`}
      fill="#FFF8C0"
      opacity={op}
    />
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────
export const HarHarGangeCTA: React.FC<{ durationInFrames?: number }> = ({ durationInFrames: durProp }) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames: compDur } = useVideoConfig();
  const dur = durProp ?? compDur;

  const fadeOutStart = dur - Math.round(fps * 1.5);
  const globalOpacity = interpolate(frame, [fadeOutStart, dur - 1], [1, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  const eaz = Easing.bezier(0.4, 0, 0.2, 1);
  const WIPE = 18;

  const textStart = 6;

  const textClip = interpolate(frame, [textStart, textStart + WIPE], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });

  const lineW   = interpolate(frame, [textStart + WIPE, textStart + WIPE + 16], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: eaz });
  const hindiOp = interpolate(frame, [textStart + WIPE + 8, textStart + WIPE + 22], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const decoStart  = textStart + WIPE + 10;
  const lotusFrame = decoStart;
  const waveFrame  = decoStart + 8;
  const waveGlow   = Math.sin(frame * 0.2) * 0.3 + 0.7;

  const textStyle: React.CSSProperties = {
    fontSize: FONT_SIZE,
    fontWeight: 900,
    fontFamily: mont,
    color: '#fff',
    letterSpacing: 12,
    lineHeight: 1,
    textShadow: '0 0 60px rgba(255,215,0,0.25)',
  };

  return (
    <AbsoluteFill style={{ opacity: globalOpacity, backgroundColor: 'transparent' }}>

      {/* River — renders first, sits behind everything */}
      <RiverBg frame={frame} fps={fps} />

      {/* Centre column */}
      <AbsoluteFill style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0, paddingTop: '5%' }}>

        {/* HAR HAR GANGE — single unbroken wipe */}
        <div style={{ overflow: 'hidden' }}>
          <div style={{ ...textStyle, clipPath: `inset(0 ${100 - textClip}% 0 0)` }}>HAR HAR GANGE</div>
        </div>

        {/* Gold underline */}
        <div style={{
          width: `${lineW * 9.2}px`,
          maxWidth: 820,
          height: 2,
          background: 'rgba(255,215,0,0.75)',
          marginTop: 10,
        }} />

        {/* Hindi */}
        <div style={{
          opacity: hindiOp,
          color: 'rgba(255,215,0,0.85)',
          fontSize: 42,
          fontFamily: noto,
          fontWeight: 700,
          letterSpacing: 2,
          marginTop: 12,
        }}>
          हर हर गंगे
        </div>

        <div style={{ height: 100 }} />
      </AbsoluteFill>

      {/* SVG overlay — sparkles + Lotus · Wave · Lotus */}
      <AbsoluteFill style={{ pointerEvents: 'none' }}>
        <svg width={1280} height={704} viewBox="0 0 1280 704" xmlns="http://www.w3.org/2000/svg">
          <Sparkle cx={80}   cy={100} r={4} phase={0}  />
          <Sparkle cx={1200} cy={110} r={4} phase={20} />
          <Sparkle cx={90}   cy={580} r={3} phase={35} />
          <Sparkle cx={1190} cy={570} r={3} phase={12} />
          <Sparkle cx={380}  cy={80}  r={3} phase={8}  />
          <Sparkle cx={900}  cy={75}  r={3} phase={28} />

          <Lotus     cx={490} cy={520} r={40} startFrame={lotusFrame}     uid="hgl" />
          <WaterWave cx={640} cy={514}        startFrame={waveFrame}      glow={waveGlow} />
          <Lotus     cx={790} cy={520} r={40} startFrame={lotusFrame + 6} uid="hgr" />
        </svg>
      </AbsoluteFill>

    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
